/**
 * Acharya AI 3.0 — context loaders + query (replaces legacy sendMessage).
 */

import axios from "axios";
import { withSkipGlobalError } from "@/lib/http/skip-global-error";
import { fetchSacredProfile } from "@/lib/sacred-alerts/sacred-alerts-api";
import { fetchJourneyProfile } from "@/lib/sacred-journey/sacred-journey-api";
import { fetchTattvaRelated, libraryGraphKey } from "@/lib/tattva/tattva-api";
import { fetchAcharyaDebug } from "./acharya-api";
import { getPreferredMeaning, getEnglishMeaning, normalizePreferredLang } from "@/lib/i18n/tri-language";

import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function maybeDoubleApiRetryUrl(url) {
  // Workaround for overlapping Cloudflare Worker routes where /api/acharya/* is shadowed,
  // but /api/api/acharya/* still reaches the intended worker.
  if (url.startsWith("/api/") && !url.startsWith("/api/api/")) {
    return `/api${url}`;
  }
  if (url.includes("/api/") && !url.includes("/api/api/")) {
    return url.replace("/api/", "/api/api/");
  }
  return null;
}

/** Auth profile (diagnostic + seeker fields) */
export async function loadUserProfile(token) {
  if (!token) return null;
  try {
    const res = await axios.get(`${API}/user`, withSkipGlobalError({ headers: authHeaders(token) }));
    return res.data;
  } catch {
    return null;
  }
}

/** Phase 2M sacred calendar profile */
export async function loadSacredUserProfile(token) {
  if (!token) return null;
  try {
    return await fetchSacredProfile(token);
  } catch {
    return null;
  }
}

/** Phase 2N journey engine output */
export async function loadJourney(token) {
  if (!token) return null;
  try {
    return await fetchJourneyProfile(token);
  } catch {
    return null;
  }
}

/** Tattva graph for primary iṣṭa / deity */
export async function loadTattvaForUser(token, sacredProfile, journey) {
  const deity =
    sacredProfile?.preferred_deities?.[0] ??
    journey?.mapping_inputs_snapshot?.preferred_deities?.[0];
  if (!deity) return null;
  try {
    const key = libraryGraphKey("deity", deity);
    return await fetchTattvaRelated(key);
  } catch {
    return null;
  }
}

/** Load profile + journey + tattva for POST /acharya/query context */
export async function loadAcharyaContext(token) {
  const [profile, sacred, journey] = await Promise.all([
    loadUserProfile(token),
    loadSacredUserProfile(token),
    loadJourney(token),
  ]);
  const tattva = await loadTattvaForUser(token, sacred, journey);
  return {
    profile: profile
      ? {
          id: profile.id,
          email: profile.email,
          name: profile.name,
          seeker_state: profile.seeker_state,
          archetype_id: profile.archetype_id,
          primary_gate: profile.primary_gate,
          adhikara_level: profile.adhikara_level,
          pathway_ids: profile.pathway_ids,
        }
      : null,
    sacred,
    journey,
    tattva,
  };
}

export async function fetchAcharyaSuggestions(token) {
  const url = `${API}/acharya/suggestions`;
  try {
    const res = await axios.get(url, withSkipGlobalError({ headers: authHeaders(token) }));
    return res.data?.suggestions ?? [];
  } catch (err) {
    if (err?.response?.status === 404) {
      const retry = maybeDoubleApiRetryUrl(url);
      if (retry) {
        const res = await axios.get(retry, withSkipGlobalError({ headers: authHeaders(token) }));
        return res.data?.suggestions ?? [];
      }
    }
    throw err;
  }
}

/**
 * Acharya 3.0 query — POST /api/acharya/query
 * @param {string} question
 * @param {string} [userId]
 * @param {string} [token]
 * @param {object} [context] optional preloaded { profile, journey, tattva }
 */
function buildAcharyaQueryBody(question, userId, ctx, language, options = {}) {
  const resolvedUserId = userId || ctx?.profile?.id || "anonymous";
  const lang =
    language ??
    ctx?.profile?.preferred_language ??
    ctx?.profile?.language ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("vedapath_lang") : null) ??
    "en";
  const profile = ctx?.profile ? { ...ctx.profile } : {};
  if (options.seekerState) {
    profile.seeker_state = options.seekerState;
  }
  const sessionId =
    options.sessionId ??
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `anon-${Date.now()}`);

  const history = Array.isArray(options.conversationHistory)
    ? options.conversationHistory
        .filter((t) => t && (t.role === "user" || t.role === "assistant") && t.content?.trim())
        .slice(-8)
        .map((t) => ({ role: t.role, content: String(t.content).trim().slice(0, 500) }))
    : [];

  return {
    body: {
      question,
      user_id: resolvedUserId,
      session_id: sessionId,
      conversation_history: history,
      flow_mode: options.flowMode !== false,
      language: lang,
      context: {
        profile: Object.keys(profile).length ? profile : null,
        journey: ctx?.journey ?? null,
        tattva: ctx?.tattva ?? null,
      },
      use_gemini: true,
    },
  };
}

export async function queryAcharya(question, userId, token, context, options = {}) {
  const ctx =
    context ??
    (token ? await loadAcharyaContext(token) : { profile: null, journey: null, tattva: null });

  const { body } = buildAcharyaQueryBody(question, userId, ctx, undefined, options);
  const url = `${API}/acharya/query`;
  try {
    const res = await axios.post(url, body, withSkipGlobalError({ headers: authHeaders(token) }));
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      const retry = maybeDoubleApiRetryUrl(url);
      if (retry) {
        const res = await axios.post(retry, body, withSkipGlobalError({ headers: authHeaders(token) }));
        return res.data;
      }
    }
    throw err;
  }
}

/**
 * Phase AA4-FIX — streaming disabled; uses full JSON from POST /acharya/query.
 * @param {(partial: { answer?: string }) => void} [onPartial] optional; not used for chunks
 */
export async function queryAcharyaStream(question, userId, token, context, onPartial) {
  const data = await queryAcharya(question, userId, token, context);
  if (data?.error) return data;
  onPartial?.({ answer: data?.answer ?? "" });
  return data;
}

/**
 * Phase 2P / 11 — Audio: POST /api/acharya/audio
 */
export function resolveAudioSrc(payload) {
  if (!payload) return null;
  if (payload.url) return payload.url;
  if (payload.audioUrl) return payload.audioUrl;
  if (payload.audio_base64 && payload.mime_type) {
    return `data:${payload.mime_type};base64,${payload.audio_base64}`;
  }
  return null;
}

export async function fetchAcharyaAudio(text, mode = "voice", token, extras) {
  const url = `${API}/acharya/audio`;
  const body = { text, mode };
  if (typeof extras === "number") body.minutes = extras;
  else if (extras && typeof extras === "object") {
    if (typeof extras.minutes === "number") body.minutes = extras.minutes;
    if (extras.audio_ref) body.audio_ref = extras.audio_ref;
    if (extras.language) body.language = extras.language;
  }
  try {
    const res = await axios.post(url, body, withSkipGlobalError({ headers: authHeaders(token) }));
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      const retry = maybeDoubleApiRetryUrl(url);
      if (retry) {
        const res = await axios.post(retry, body, withSkipGlobalError({ headers: authHeaders(token) }));
        return res.data;
      }
    }
    throw err;
  }
}

/** Phase AV — meaning / story / commentary TTS with language + English fallback text. */
export async function fetchMeaningAudio(text, mode = "meaning", language = "en", token) {
  return fetchAcharyaAudio(text, mode, token, { language });
}

/** Resolve listen text + language from a tri-language item. */
export function resolveListenMeaning(item, preferredLang = "en", meaningPrefix = "translation") {
  const prefLang = normalizePreferredLang(preferredLang);
  const preferred = getPreferredMeaning(item, prefLang, meaningPrefix);
  const english = getEnglishMeaning(item, meaningPrefix);
  const language =
    prefLang !== "en" && preferred && english && preferred !== english ? prefLang : "en";
  return { text: preferred || english, english, language };
}

const FRIENDLY_ERROR =
  "Āchārya could not respond right now. Please try again in a moment. Your question has been noted.";

export function getAcharyaFriendlyError(err) {
  return err?.response?.data?.detail || err?.response?.data?.error || err?.message || FRIENDLY_ERROR;
}

/** Log failure for admins (console + optional debug fetch) */
export async function logAcharyaError(question, err, { userId, email } = {}) {
  const detail = getAcharyaFriendlyError(err);
  console.error("[Acharya]", { question, userId, detail, status: err?.response?.status });
  try {
    if (email || userId) {
      await fetchAcharyaDebug(email, question).catch(() => null);
    }
  } catch {
    /* admin debug only */
  }
  return detail;
}
